import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { handlePulseBriefClick } from "@/lib/pulseBriefCta";

/**
 * MobileStickyCTA — a slim, mobile-only fixed bar that appears after the
 * visitor scrolls past the hero and disappears when either the lead form
 * or the mobile navigation menu is on-screen.
 *
 * Constraints:
 *   - mobile-only (md:hidden)
 *   - safe-area-inset aware (uses env(safe-area-inset-bottom))
 *   - hides when #pulse-brief is intersecting the viewport
 *   - hides when the mobile Sheet menu is open
 *     (SiteHeader mirrors its open state to `documentElement.dataset.mobileMenuOpen`)
 *   - disable-able via localStorage flag: `pulse.hideStickyCta`
 *   - 48–52px touch height
 */
export default function MobileStickyCTA() {
    const [visible, setVisible] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [disabled, setDisabled] = useState(false);

    // Respect a manual disable flag — easy way to hide the bar if testing
    // shows it feels intrusive. Set via:
    //   window.localStorage.setItem('pulse.hideStickyCta', '1')
    useEffect(() => {
        try {
            const flag = window.localStorage.getItem("pulse.hideStickyCta");
            if (flag === "1" || flag === "true") setDisabled(true);
        } catch (e) { /* ignore */ }
    }, []);

    // Show after scroll passes the hero (~92vh). Uses scrollY threshold to
    // avoid mounting a full IntersectionObserver on the hero element which
    // may not always be present.
    useEffect(() => {
        let raf = null;
        function onScroll() {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                const threshold = Math.max(window.innerHeight * 0.7, 500);
                setVisible(window.scrollY > threshold);
                raf = null;
            });
        }
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, []);

    // Hide when the on-page lead form is in view. Uses IntersectionObserver
    // on the existing `#pulse-brief` anchor.
    useEffect(() => {
        if (typeof IntersectionObserver === "undefined") return undefined;
        const el = document.getElementById("pulse-brief");
        if (!el) return undefined;
        const io = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    setFormVisible(entry.isIntersecting);
                }
            },
            { threshold: 0.15 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    // Hide when the mobile Sheet menu is open. SiteHeader mirrors its
    // open/closed state to <html data-mobile-menu-open="true">.
    useEffect(() => {
        if (typeof MutationObserver === "undefined") return undefined;
        const root = document.documentElement;
        const read = () => setMenuOpen(root.dataset.mobileMenuOpen === "true");
        read();
        const mo = new MutationObserver(read);
        mo.observe(root, { attributes: true, attributeFilter: ["data-mobile-menu-open"] });
        return () => mo.disconnect();
    }, []);

    if (disabled) return null;
    const shouldShow = visible && !formVisible && !menuOpen;

    return (
        <div
            aria-hidden={shouldShow ? undefined : "true"}
            data-testid="mobile-sticky-cta"
            className={`md:hidden fixed left-0 right-0 z-40 px-3 transition-all duration-300 will-change-transform ${shouldShow ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
            style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
        >
            <div className="mx-auto max-w-md rounded-full border border-white/15 bg-black/85 backdrop-blur-md shadow-[0_14px_40px_-12px_rgba(255,42,42,0.35)] flex items-center justify-between gap-3 pl-5 pr-2 py-2">
                <div className="flex flex-col leading-tight">
                    <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/60">
                        Have a brief?
                    </span>
                    <span className="font-display text-base text-white">
                        Start here.
                    </span>
                </div>
                <Link
                    to="/contact"
                    onClick={handlePulseBriefClick}
                    aria-label="Start your Pulse brief"
                    data-testid="mobile-sticky-cta-button"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold text-sm px-4 min-h-11 h-11"
                >
                    Start
                    <ArrowUpRight size={14} />
                </Link>
            </div>
        </div>
    );
}
